import {
  alpha,
  Box,
  Card,
  CardContent,
  Checkbox,
  Collapse,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

export interface ExtraOptionCardProps {
  option: {
    value: string;
    label: string;
    description?: string;
    fee?: number;
    minLbs?: number;
    pricePerPound?: number;
    type?: string;
  };
  checked?: boolean;
  disabled?: boolean;
  quantity?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuantityChange?: (value: string, quantity: number) => void;
}

export function ExtraOptionCard({
  option,
  checked,
  disabled,
  quantity,
  onChange,
  onQuantityChange,
}: ExtraOptionCardProps) {
  const handleCardClick = () => {
    if (disabled || !onChange) return;

    const syntheticEvent = {
      target: {
        name: option.value,
        value: option.value,
        checked: !checked,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (onQuantityChange) {
      onQuantityChange(option.value, newQuantity);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount ?? 0);

  const getPricingInfo = () => {
    if (option.type === 'pricedPerPound') {
      const price = formatCurrency(option.pricePerPound ?? 0);
      const min =
        Number.isFinite(option.minLbs) && option.minLbs! > 0
          ? ` • ${option.minLbs} lbs minimum`
          : '';
      return `${price} per lb${min}`;
    }
    if (option.type === 'flatFee') {
      return formatCurrency(option.fee ?? 0);
    }
    return '';
  };

  const calculateEstimatedCost = () => {
    if (option.type === 'pricedPerPound' && quantity && option.pricePerPound) {
      return quantity * option.pricePerPound;
    }
    return 0;
  };

  const pricingInfo = getPricingInfo();
  const estimatedCost = calculateEstimatedCost();
  const showQuantityInput = checked && option.type === 'pricedPerPound';

  return (
    <Card
      data-testid="extra-option-card"
      onClick={handleCardClick}
      sx={{
        borderRadius: '9px',
        my: 2,
        width: '100%',
        border: '2px solid', // Always 2px border to prevent layout shift
        borderColor: checked ? 'primary.main' : 'divider', // Divider color when not selected

        // selected state
        ...(checked
          ? {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.1),
            }
          : {}),

        // hover (only when not disabled)
        ...(disabled
          ? { opacity: 0.7, cursor: 'not-allowed' }
          : {
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: (theme) =>
                  checked
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05),
              },
            }),

        // Ensure all child elements use pointer cursor when not disabled
        ...(!disabled && {
          '& *': {
            cursor: 'pointer !important',
          },
        }),
      }}
      variant="outlined"
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Grid container spacing={1} alignItems="center" wrap="nowrap">
          <Grid sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              name={option.value}
              value={option.value}
              checked={checked}
              disabled={disabled}
              onChange={onChange}
              size="medium"
            />
          </Grid>
          <Grid sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'text.primary',
                fontSize: '1.125rem',
                fontWeight: '500',
                lineHeight: '1.25rem',
                mb: option.description ? 0.5 : 0,
              }}
            >
              {option.label}
            </Typography>
            {option.description && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                }}
              >
                {option.description}
              </Typography>
            )}
            {pricingInfo && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: '1.25rem',
                  mt: option.description ? 0.5 : 0,
                }}
              >
                {pricingInfo}
              </Typography>
            )}
          </Grid>
        </Grid>

        {/* Quantity input for pricedPerPound type */}
        <Collapse in={showQuantityInput} timeout="auto" unmountOnExit>
          <Box sx={{ pl: 4, pr: 2, pb: 2, pt: 2 }}>
            <TextField
              size="small"
              label="Quantity (lbs)"
              type="number"
              value={quantity || option.minLbs || 0}
              disabled={disabled}
              onClick={(e) => e.stopPropagation()} // Prevent card click when interacting with input
              onChange={(e) => handleQuantityChange(Number(e.target.value))}
              inputProps={{
                min: option.minLbs || 0,
                step: 1,
              }}
              sx={{ flex: 1, maxWidth: 150 }}
            />

            {estimatedCost > 0 && (
              <Box sx={{ minWidth: 120, pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Estimated Total
                </Typography>
                <Typography variant="h6" color="primary">
                  {formatCurrency(estimatedCost)}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}
